import i18n from 'i18next';

/**
 * Named keys for each koalafi.com disclosure link routed through getDisclosureUrl.
 * Pass these constants (never raw strings) so typos are caught at compile time and
 * each path is defined in exactly one place.
 */
export const DisclosureLink = {
  AuthorizedUserTerms: 'authorized-user-terms',
  CreditPullAuth: 'credit-pull-auth',
  WhatCanILease: 'what-can-i-lease',
  PlaidAuthorization: 'plaid-authorization',
  TermsAndConditions: 'terms-and-conditions',
  CustomerIdProgram: 'customer-id-program',
  PrivacyPolicy: 'privacy-policy',
  ElectronicCommunications: 'electronic-communications',
} as const;

type DisclosureLinkPath = (typeof DisclosureLink)[keyof typeof DisclosureLink];

/**
 * Disclosure paths that have a Spanish (/es/) version. When the app is displayed in
 * Spanish, these route to their /es/ page; otherwise the default (English) page is used.
 *
 * To add Spanish routing for another disclosure link in the future:
 *   1. Add its key to DisclosureLink above (value = the English path).
 *   2. Add an entry here mapping that key to the exact /es/ path from the mapping doc.
 *   3. Point the call site at getDisclosureUrl(DisclosureLink.<Key>) if it isn't already.
 */
const BASE_URL = 'https://koalafi.com';

const SPANISH_PATHS: Partial<Record<DisclosureLinkPath, string>> = {
  [DisclosureLink.AuthorizedUserTerms]: 'es/authorized-user-terms/',
  [DisclosureLink.CreditPullAuth]: 'es/credit-pull-auth',
  [DisclosureLink.WhatCanILease]: 'es/what-can-i-lease',
  [DisclosureLink.PlaidAuthorization]: 'es/plaid-authorization',
  [DisclosureLink.TermsAndConditions]: 'es/terms-and-conditions',
  [DisclosureLink.CustomerIdProgram]: 'es/customer-id-program',
  [DisclosureLink.PrivacyPolicy]: 'es/privacy-policy',
  [DisclosureLink.ElectronicCommunications]: 'es/electronic-communications',
};

export const getDisclosureUrl = (path: DisclosureLinkPath): string => {
  const spanishPath = i18n.resolvedLanguage === 'es' ? SPANISH_PATHS[path] : undefined;
  return `${BASE_URL}/${spanishPath ?? path}`;
};
